import { ElementType, FC, ReactNode } from 'react';

import { BasePaginatedDrawerProps, PageComponentProps } from '..';
import { MeasuresContent } from './MeasuresContent';
import { MeasuresDrawer } from './MeasuresDrawer';
import { MeasureSchemaAndAnswers } from './types';

export type PendingMeasuresFormProps = {
  /**
   * The measure pages to render. `undefined` indicates the measures are still
   * loading; an empty array indicates there are no outstanding measures.
   */
  pages: MeasureSchemaAndAnswers[] | undefined;
  /**
   * Renders the per-measure form. Defaults to the shared `MeasuresContent`.
   */
  contentComponent?: FC<PageComponentProps<MeasureSchemaAndAnswers>>;
  /**
   * Renders the submit / navigation actions for each measure. Callers supply a
   * wrapper that wires up the appropriate submission behavior + completion
   * campaign for their use case (waiting-room drawer, soft block, etc.).
   */
  actionsComponent: FC<PageComponentProps<MeasureSchemaAndAnswers>>;
  /**
   * Rendered while `pages` is `undefined` (measures still loading).
   */
  loadingFallback?: ReactNode;
  /**
   * Rendered when `pages` is an empty array (no outstanding measures).
   */
  emptyFallback?: ReactNode;
  /**
   * The page to display initially. Clamped to the available page range so a
   * stale saved index can never point past the last measure.
   */
  initialPageIndex?: number;
  /**
   * Called whenever the current page changes, so callers can persist progress.
   */
  onPageIndexChange?: (pageIndex: number) => void;
  drawerBodyProps?: BasePaginatedDrawerProps<MeasureSchemaAndAnswers>['drawerBodyProps'];
  headingAs?: ElementType;
};

/**
 * Shared component that renders a paginated set of pending
 * measure forms (PHQ-9, GAD-7, etc.) along with their submit / navigation
 * actions. This decouples the measure-rendering experience from the waiting-room
 * drawer so the same form can be reused across surfaces (e.g. the soft-block
 * pre-session flow). Each surface injects its own data source, content/actions
 * components, and loading / empty states.
 */
export function PendingMeasuresForm({
  pages,
  contentComponent = MeasuresContent,
  actionsComponent,
  loadingFallback = null,
  emptyFallback = null,
  initialPageIndex = 0,
  onPageIndexChange,
  drawerBodyProps,
  headingAs,
}: PendingMeasuresFormProps) {
  // Pages is initially undefined before measures finishes loading
  if (!pages) {
    return <>{loadingFallback}</>;
  }

  // Once loaded, if there are no pages, there are no outstanding measures
  if (!pages.length) {
    return <>{emptyFallback}</>;
  }

  const clampedInitialPageIndex = Math.min(initialPageIndex, pages.length - 1);

  return (
    <MeasuresDrawer
      pages={pages}
      headingAs={headingAs}
      initialPageIndex={clampedInitialPageIndex}
      contentComponent={contentComponent}
      actionsComponent={actionsComponent}
      drawerBodyProps={drawerBodyProps}
      onPageIndexChange={onPageIndexChange}
    />
  );
}
